import { useState } from "react";
import { Link } from "react-router-dom";
import { Dropdown } from "@/config/antd";
import Delete from "@/components/common/alerts/Delete";
import PropTypes from "prop-types";
import CustomMoreOptions from "@/components/specific/Custom_More_Options";
import WebIcon from "@/components/common/Web_Icons";
import { isScheduleExpired } from "@/utils/calendar_utils";

const EventCard = ({ event, onDelete, onModify, ...restProps }) => {
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);

  // Prefer full schedule when available; fall back to card-mapped endDate/endTime
  const currentDate = new Date();
  const isExpired = isScheduleExpired(
    event.schedule || { endDate: event.endDate, endTime: event.endTime },
    currentDate
  );

  const handleMenuClick = (menuItem, e) => {
    e?.domEvent?.stopPropagation();
    if (menuItem.key === "delete") {
      setIsDeleteModalVisible(true);
    } else if (menuItem.key === "modify") {
      onModify(event._id);
    }
  };

  const handleDeleteConfirm = () => {
    setIsDeleteModalVisible(false);
    onDelete(event._id, event.title);
  };

  const menuItems = {
    items: [
      {
        key: "modify",
        label: "Modify",
      },
      {
        key: "delete",
        label: "Delete",
      },
    ],
    onClick: handleMenuClick,
  };

  return (
    <>
      <div
        className={`w-full  transition-shadow p-4 bg-[var(--header-bg)] rounded-lg shadow-sm cursor-pointer hover:shadow-md ${
          isExpired ? "opacity-60" : ""
        }`}
        data-testid="event-card"
        {...restProps}>
        <div className="flex items-center justify-between">
          <Link
            to={`/dashboard/events/${event._id}`}
            className="flex items-center space-x-4 flex-grow"
            data-testid="event-link">
            <div className="relative flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16">
              <svg className="w-full h-full transform -rotate-90">
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  stroke="#E5E7EB"
                  strokeWidth="4"
                  fill="none"
                />
                <circle
                  cx="50%"
                  cy="50%"
                  r="45%"
                  stroke={
                    isExpired ? "var(--color-danger, #FF4D4F)" : "#1890FF"
                  }
                  strokeWidth="4"
                  fill="none"
                  strokeDasharray={175.93}
                  strokeDashoffset={175.93 * (1 - event.progress / 100)}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span
                  className={`text-base font-semibold sm:text-lg ${
                    isExpired ? "text-danger" : "text-primary"
                  }`}
                  data-testid="event-progress">
                  {event.progress}%
                </span>
              </div>
            </div>

            <div className="flex flex-col justify-start text-left align-start whitespace-nowrap">
              <span
                className="text-small sm:text-sm text-primary"
                data-testid="event-id">
                #{event.id}
              </span>
              <h3
                className="mt-1 font-semibold text-bold sm:text-lg text-darkGrey truncate max-w-[200px]"
                data-testid="event-title">
                {event.title}
              </h3>
              <div
                className="flex items-center mt-2 text-darkGrey text-small sm:text-sm"
                data-testid="event-date">
                <WebIcon icon="CalenderFilled" size="small" className="mr-1" />
                <span>Published on {event.publishDate}</span>
              </div>
              {isExpired && (
                <div className="flex items-center mt-1">
                  <div className="w-2 h-2 rounded-full bg-danger mr-1" />
                  <p className="text-small text-danger font-bold">Expired</p>
                </div>
              )}
            </div>
          </Link>

          <button
            className="self-center p-2 transition-colors rounded-full sm:p-3 hover:bg-gray-100"
            aria-label="More options"
            onClick={(e) => e.stopPropagation()}
            data-testid="event-menu-button">
            <Dropdown
              menu={menuItems}
              trigger={["click"]}
              onClick={(e) => e.stopPropagation()}
              data-testid="event-menu-dropdown">
              <CustomMoreOptions variant="filled" orientation="vertical" />
            </Dropdown>
          </button>
        </div>
      </div>

      <Delete
        isVisible={isDeleteModalVisible}
        onClose={() => setIsDeleteModalVisible(false)}
        onProceed={handleDeleteConfirm}
        header="Delete Event?"
        paragraph={`Are you sure you want to delete the event "${event.title}" from your existing list?`}
        proceedButtonText="Delete"
        cancelButtonText="Cancel"
        data-testid="delete-event-modal"
      />
    </>
  );
};

EventCard.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.string,
    _id: PropTypes.string,
    title: PropTypes.string,
    progress: PropTypes.number,
    publishDate: PropTypes.string,
    endDate: PropTypes.string,
    endTime: PropTypes.string,
    schedule: PropTypes.object,
  }),
  onDelete: PropTypes.func,
  onModify: PropTypes.func,
};

export default EventCard;
